/**
 * 1.7.9 — New event's last step, "Tell people" (D-1.7.9-7).
 *
 * The event is saved already; this step never blocks it. It asks for the announcement's draft about the event
 * (`announceEventReq` — the 1.7.6 wording from the event's fields, English and Spanish, to all tenants with an
 * email), shows it in the 1.7.6 message box to edit (with Clean it up), and offers Preview and Send — or Later, and
 * then the draft simply waits under Announcements and on the event's Reminders card.
 *
 * Nothing is sent by opening this step. Send is a person's press, said twice ("Send to n people"), and it goes
 * through the Outbox and its delay like every announcement. If the draft cannot be made, the step says so and offers
 * Done: the event is saved all the same.
 *
 * The sheet's buttons live in its footer, so this is a hook that answers the step's body and its footer.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ANNOUNCE_MAX_SUBJECT, type AnnounceLang, type AnnouncementDetail, type AnnouncementInput, type AnnouncementPreview } from '@shared/announcements';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { StepHead } from '@/components/Steps';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { previewAnnouncement, requestAudience, saveAnnouncement, sendAnnouncement, useAnnouncementRefresh } from '@/features/announcements/api';
import { DraftBox } from '@/features/announcements/DraftBox';
import { errorText } from '@/features/people/api';
import { useCalendarRefresh } from './api177';
import { announceEventReq } from './api179';

/** The event New event just saved. */
export interface ToldEvent {
  eventId: string;
  name: string;
  /** It was the first of a repeat: the announcement is about this first one only. */
  repeat: boolean;
  /** People answer (RSVP): each person's own link goes with Invite, on the event's page. */
  rsvp: boolean;
}

type Two = { en: string; es: string };

export function useTellPeople(saved: ToldEvent | null, onDone: () => void): { body: ReactNode; footer: ReactNode } {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const refreshAnnouncements = useAnnouncementRefresh();
  const refreshCalendar = useCalendarRefresh();
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [why, setWhy] = useState('');
  const [draft, setDraft] = useState<AnnouncementDetail | null>(null);
  const [subject, setSubject] = useState<Two>({ en: '', es: '' });
  const [body, setBody] = useState<Two>({ en: '', es: '' });
  const [preview, setPreview] = useState<{ key: string; data: AnnouncementPreview } | null>(null);
  const [previewLang, setPreviewLang] = useState<AnnounceLang>('en');
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState<'preview' | 'send' | 'save' | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  /** Later could not keep the edits: the next press closes all the same. */
  const [saveFailed, setSaveFailed] = useState(false);
  /** D-1028-ONELANG: how many of the people it reaches have Spanish on their own record. */
  const [spanishCount, setSpanishCount] = useState<{ count: number; total: number } | null>(null);
  const asked = useRef<string | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const eventId = saved?.eventId ?? null;
  const hasSpanish = !!body.es.trim() && !!subject.es.trim();
  useEffect(() => {
    if (!draft) return;
    let live = true;
    requestAudience(draft.audience, hasSpanish)
      .then((a) => {
        if (live) setSpanishCount({ count: a.spanishMarked, total: a.total });
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [draft, hasSpanish]);
  useEffect(() => {
    // once an event: the draft is asked for when the step opens (a second ask answers the same draft)
    if (!eventId || asked.current === eventId) return;
    asked.current = eventId;
    setState('loading');
    announceEventReq(eventId)
      .then((r) => {
        if (!alive.current) return;
        setDraft(r.announcement);
        setSubject({ en: r.announcement.subject.en, es: r.announcement.subject.es });
        setBody({ en: r.announcement.body.en, es: r.announcement.body.es });
        setState('ready');
        refreshAnnouncements(r.announcement.id);
        refreshCalendar(eventId);
        announce(t('ev179r.tell.ready'));
      })
      .catch((err: unknown) => {
        if (!alive.current) return;
        const m = errorText(err);
        setWhy(m);
        setState('failed');
        announce(t('ev179r.tell.failed', { why: m }), 'assertive');
      });
    // the calls of this render are the ones to use; the effect runs again only for another event
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  if (!saved) return { body: null, footer: null };

  const input: AnnouncementInput | null = draft ? { id: draft.id, subject: { en: subject.en.trim(), es: subject.es.trim() }, body, audience: draft.audience, eventId: draft.eventId ?? saved.eventId, fields: draft.fields } : null;
  const inputKey = JSON.stringify(input);
  const dirty = !!draft && (subject.en.trim() !== draft.subject.en || subject.es.trim() !== draft.subject.es || body.en !== draft.body.en || body.es !== draft.body.es);
  const shown = preview && preview.key === inputKey ? preview.data : null;
  const shownText = shown ? (previewLang === 'es' && shown.es ? shown.es : shown.en) : null;
  const langName = (l: AnnounceLang) => t(l === 'es' ? 'ev179r.tell.spanish' : 'ev179r.tell.english');

  const bad = (m: string) => {
    setProblem(m);
    announce(m, 'assertive');
  };
  /** The message as a tenant will see it, and how many it reaches; answers it, or null when it could not be made. */
  const makePreview = async (): Promise<AnnouncementPreview | null> => {
    if (!input) return null;
    setBusy('preview');
    setProblem(null);
    try {
      const data = await previewAnnouncement(input);
      if (!alive.current) return null;
      setPreview({ key: inputKey, data });
      if (!data.es) setPreviewLang('en');
      return data;
    } catch (err) {
      if (alive.current) bad(errorText(err));
      return null;
    } finally {
      if (alive.current) setBusy(null);
    }
  };
  const onPreview = async () => {
    setSure(false);
    const data = await makePreview();
    if (!data) return;
    announce(data.blocked ?? t('ev179r.tell.previewTitle'), data.blocked ? 'assertive' : 'polite');
    requestAnimationFrame(() => document.getElementById('ne-tell-preview-h')?.focus());
  };
  /** The first press of Send: the count is read (a fresh preview) and said; nothing is sent yet. */
  const onSend = async () => {
    if (!input) return;
    if (!input.subject.en && !input.subject.es) return bad(t('ev179r.tell.needSubject'));
    if (!input.body.en.trim()) return bad(t('ev179r.tell.needBody'));
    const data = shown ?? (await makePreview());
    if (!data) return;
    if (data.blocked) return bad(data.blocked);
    setSure(true);
    announce(t('ev179r.tell.sureAsk', { count: data.audience.total }));
  };
  const onSendSure = async () => {
    if (!input) return;
    setBusy('send');
    try {
      const r = await sendAnnouncement(input);
      toast(r.message);
      announce(r.message);
      refreshAnnouncements(r.announcement.id);
      refreshCalendar(saved.eventId);
      onDone();
    } catch (err) {
      // a refusal (nobody to send to, no To set): the server's own words; the event stays saved and the draft stays
      if (alive.current) {
        bad(errorText(err));
        setSure(false);
      }
    } finally {
      if (alive.current) setBusy(null);
    }
  };
  /** Later: the draft waits under Announcements — with what was typed here, when anything was. */
  const onLater = async () => {
    if (!input || !dirty || saveFailed) return onDone();
    setBusy('save');
    try {
      const d = await saveAnnouncement(input);
      refreshAnnouncements(d.id);
      onDone();
    } catch (err) {
      if (alive.current) {
        setSaveFailed(true);
        bad(t('ev179r.tell.saveFailed', { why: errorText(err) }));
      }
    } finally {
      if (alive.current) setBusy(null);
    }
  };

  const body_ = (
    <div className="ev179r-tell" data-testid="ne-tell">
      <StepHead n={3} of={3} question={t('ev179r.tell.q')} id="ne-step-h" testId="ne-step-3" />
      <p className="cal177n-line" data-testid="ne-tell-saved">
        <Icon name="check" width={18} height={18} />
        <span>{t('ev179r.tell.saved', { name: saved.name })}</span>
      </p>
      {saved.repeat ? (
        <p className="cal177n-line" data-testid="ne-tell-first">
          <Icon name="info" width={18} height={18} />
          <span>{t('ev179r.tell.firstOnly')}</span>
        </p>
      ) : null}
      <div role="status">
        {state === 'loading' ? (
          <p className="help" data-testid="ne-tell-loading">
            {t('ev179r.tell.loading')}
          </p>
        ) : null}
      </div>
      {state === 'failed' ? (
        <p className="cal177n-line" role="alert" data-testid="ne-tell-failed">
          <Icon name="alert" width={18} height={18} />
          <span>{t('ev179r.tell.failed', { why })}</span>
        </p>
      ) : null}
      {state === 'ready' && draft ? (
        <>
          <p className="help" data-testid="ne-tell-who">
            {t('ev179r.tell.who')}
          </p>
          {saved.rsvp ? (
            <p className="help" data-testid="ne-tell-rsvp">
              {t('ev179r.tell.rsvp')}
            </p>
          ) : null}
          <div className="field">
            <label htmlFor="ne-tell-subject-en">{t('ev179r.tell.subjectEn')}</label>
            <input id="ne-tell-subject-en" className="input" lang="en" value={subject.en} onChange={(e) => setSubject((s) => ({ ...s, en: e.target.value }))} maxLength={ANNOUNCE_MAX_SUBJECT} autoComplete="off" disabled={busy === 'send'} data-testid="ne-tell-subject-en" />
          </div>

          <DraftBox
            value={body}
            onChange={setBody}
            request={{ eventId: draft.eventId ?? saved.eventId, fields: draft.fields ?? undefined }}
            disabled={busy === 'send'}
            spanish={spanishCount}
            spanishExtra={
            <div className="field">
              <label htmlFor="ne-tell-subject-es">{t('ev179r.tell.subjectEs')}</label>
              <input id="ne-tell-subject-es" className="input" lang="es" value={subject.es} onChange={(e) => setSubject((s) => ({ ...s, es: e.target.value }))} maxLength={ANNOUNCE_MAX_SUBJECT} autoComplete="off" disabled={busy === 'send'} data-testid="ne-tell-subject-es" />
            </div>
            }
          />
          {preview ? (
            <section aria-labelledby="ne-tell-preview-h" className="ev179r-preview" data-testid="ne-tell-preview-panel">
              <h3 id="ne-tell-preview-h" tabIndex={-1}>
                {t('ev179r.tell.previewTitle')}
              </h3>
              {!shown || !shownText ? (
                <p className="cal177n-line" data-testid="ne-tell-preview-old">
                  <Icon name="info" width={18} height={18} />
                  <span>{t('ev179r.tell.previewOld')}</span>
                </p>
              ) : (
                <>
                  {shown.es ? (
                    <div className="seg" role="group" aria-label={t('ev179r.tell.previewLang')}>
                      {(['en', 'es'] as const).map((l) => (
                        <button key={l} type="button" aria-pressed={previewLang === l} onClick={() => setPreviewLang(l)} data-testid={`ne-tell-preview-lang-${l}`}>
                          {langName(l)}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <p className="help" data-testid="ne-tell-preview-subject">
                    {t('ev179r.tell.previewSubject', { subject: shownText.subject })}
                  </p>
                  <div className="ann176-frame">
                    <iframe title={t('ev179r.tell.previewFrame', { lang: langName(shown.es ? previewLang : 'en') })} srcDoc={shownText.html} sandbox="" data-testid="ne-tell-preview-frame" />
                  </div>
                  <p data-testid="ne-tell-preview-count">{t('ev179r.tell.previewCount', { people: t('ann176.n.people', { count: shown.audience.total }), minutes: shown.audience.delayMinutes })}</p>
                </>
              )}
            </section>
          ) : null}
        </>
      ) : null}
      {problem ? (
        <p className="field-err" role="alert" data-testid="ne-tell-problem">
          <Icon name="alert" width={16} height={16} /> {problem}
        </p>
      ) : null}
    </div>
  );

  const footer =
    state !== 'ready' ? (
      <Button onClick={onDone} data-testid="ne-tell-later">
        {state === 'failed' ? t('ev179r.tell.done') : t('ev179r.tell.later')}
      </Button>
    ) : sure ? (
      <>
        <Button variant="secondary" disabled={!!busy} onClick={() => setSure(false)} data-testid="ne-tell-not-now">
          {t('ev179r.tell.notNow')}
        </Button>
        <Button disabled={!!busy || !shown || !!shown.blocked} onClick={() => onSendSure()} data-testid="ne-tell-send-sure">
          {busy === 'send' ? t('ev179r.tell.working') : t('ev179r.tell.sendSure', { count: shown?.audience.total ?? 0 })}
        </Button>
      </>
    ) : (
      <>
        <Button variant="secondary" disabled={!!busy} onClick={() => onLater()} data-testid="ne-tell-later">
          {busy === 'save' ? t('ev179r.tell.working') : saveFailed ? t('ev179r.tell.done') : t('ev179r.tell.later')}
        </Button>
        <Button variant="secondary" disabled={!!busy} onClick={() => onPreview()} data-testid="ne-tell-preview">
          {busy === 'preview' ? t('ev179r.tell.working') : t('ev179r.tell.preview')}
        </Button>
        <Button disabled={!!busy} onClick={() => onSend()} data-testid="ne-tell-send">
          <Icon name="mail" /> {t('ev179r.tell.send')}
        </Button>
      </>
    );

  return { body: body_, footer };
}
